import { useEffect, useState } from "react";
import { Search, Mail, Phone, Copy, Check } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import api from "../../api";

// NL Labs: leads from the mindconnect.in contact form (contactus table,
// backend routes/analytics/admin_inbox.py). "Interest" is the form's
// "I'm interested in" choice, stored as a tag on the enquiry.
const INTERESTS = [
  { key: "", label: "All" },
  { key: "practice", label: "Practice" },
  { key: "training", label: "Training" },
  { key: "both", label: "Practice + Training" },
  { key: "none", label: "Not stated" },
];
const INTEREST_TONES = {
  practice: "bg-primary-100 text-primary-800",
  training: "bg-amber-100 text-amber-800",
  both: "bg-emerald-100 text-emerald-800",
};

function CopyText({ icon: Icon, text }) {
  const [done, setDone] = useState(false);
  if (!text) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* the text stays selectable */ }
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-secondary-700 min-w-0">
      <Icon className="w-4 h-4 text-secondary-400 shrink-0" />
      <span className="select-all break-all">{text}</span>
      <button type="button" onClick={copy} aria-label={`Copy ${text}`} className="text-secondary-400 hover:text-secondary-700">
        {done ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </span>
  );
}

export default function AdminEnquiries() {
  const [interest, setInterest] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api.fetchContactEnquiries({ page, per_page: 25, interest: interest || undefined, search: query || undefined })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || "Couldn't load enquiries."));
  }, [page, interest, query]);

  return (
    <DashboardLayout>
      <div className="container mx-auto p-4 md:p-8 max-w-5xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Website Enquiries</h1>
          <p className="text-sm text-gray-500 mt-1">Messages from the contact form on mindconnect.in, newest first.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <form className="relative flex-1" onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(search.trim()); }}>
            <Search className="w-4 h-4 text-secondary-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone or message"
              className="w-full rounded-lg border border-secondary-300 pl-9 pr-3 py-2 text-sm text-secondary-900" />
          </form>
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map((i) => (
              <button key={i.key} type="button" onClick={() => { setInterest(i.key); setPage(1); }}
                className={`text-sm px-3 py-1.5 rounded-full border ${interest === i.key ? "bg-primary-600 text-white border-primary-600" : "bg-white text-secondary-700 border-secondary-300 hover:border-primary-400"}`}>
                {i.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-red-600">{error}</p>}
        {!data && !error && <p className="text-secondary-500">Loading…</p>}
        {data && data.enquiries.length === 0 && <p className="text-secondary-500">No enquiries here.</p>}
        {data && data.total > 0 && <p className="text-sm text-secondary-500">{data.total} enquir{data.total === 1 ? "y" : "ies"}</p>}

        <div className="space-y-3">
          {(data?.enquiries || []).map((e) => (
            <div key={e.id} className="rounded-xl border border-secondary-200 bg-white p-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="font-semibold text-secondary-900">{e.name}</p>
                <div className="flex items-center gap-2">
                  {e.interest && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${INTEREST_TONES[e.interest]}`}>
                      {INTERESTS.find((i) => i.key === e.interest)?.label}
                    </span>
                  )}
                  <span className="text-xs text-secondary-500">
                    {e.created_at ? new Date(e.created_at.replace(" ", "T") + "Z").toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                <CopyText icon={Mail} text={e.emailid} />
                <CopyText icon={Phone} text={e.phonenumber} />
              </div>
              <p className="text-sm text-secondary-700 whitespace-pre-line break-words">{e.message}</p>
            </div>
          ))}
        </div>

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-2 rounded-lg border border-secondary-300 text-sm disabled:opacity-50">Previous</button>
            <span className="text-sm text-secondary-600">Page {page} of {data.pages}</span>
            <button type="button" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-2 rounded-lg border border-secondary-300 text-sm disabled:opacity-50">Next</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
